export const ANCHOR_EMOJI = '\u2693'

const TYPEABLE_BLOCK_SELECTOR = 'p, h1, h2, h3, h4, h5, h6, li, div[data-block="text"]'
const SUBSTANTIVE_CHILD_SELECTOR = 'img, table, iframe, video, hr, a, svg'

function mapHtml(html, mutateRoot) {
    if (html == null || typeof html !== 'string') return html
    const root = document.createElement('div')
    root.innerHTML = html
    mutateRoot(root)
    return root.innerHTML
}

function isNamedAnchor(el) {
    if (!el || el.nodeType !== 1 || el.tagName !== 'A') return false
    return Boolean(el.getAttribute('name') || el.getAttribute('data-rx-anchor') === 'true')
}

function isVisuallyEmptyBlock(el) {
    if (!el || el.nodeType !== 1) return false
    if (el.querySelector(SUBSTANTIVE_CHILD_SELECTOR)) return false
    const text = (el.textContent || '')
        .replace(/[\uFEFF\u200B]/g, '')
        .replace(/\u00A0/g, ' ')
        .trim()
    return !text
}

/**
 * Named anchors in the editor: non-editable marker so they stay visible and
 * are not treated as empty links.
 */
export function decorateAnchorElement(el) {
    if (!isNamedAnchor(el)) return
    el.setAttribute('data-rx-anchor', 'true')
    el.setAttribute('contenteditable', 'false')
    el.classList.add('rx-anchor')
    const text = (el.textContent || '').replace(/\u200B/g, '').trim()
    if (!text) el.textContent = ANCHOR_EMOJI
}

/**
 * Named anchors in stored / email HTML: empty <a name> with no editor chrome.
 */
export function stripAnchorElement(el) {
    if (!isNamedAnchor(el)) return
    el.removeAttribute('contenteditable')
    el.removeAttribute('data-rx-anchor')
    el.classList.remove('rx-anchor')
    if (!el.getAttribute('class')) el.removeAttribute('class')
    const text = (el.textContent || '').replace(/\u200B/g, '').trim()
    if (!text || text === ANCHOR_EMOJI) el.textContent = ''
}

export function decorateAnchorsInRoot(root) {
    if (!root || !root.querySelectorAll) return
    root.querySelectorAll('a[name], a[data-rx-anchor]').forEach(decorateAnchorElement)
}

export function stripAnchorsInRoot(root) {
    if (!root || !root.querySelectorAll) return
    root.querySelectorAll('a[name], a[data-rx-anchor]').forEach(stripAnchorElement)
}

function setEmptyParagraphs(root, editorMode) {
    if (!root || !root.querySelectorAll) return
    root.querySelectorAll('p').forEach((el) => {
        if (!isVisuallyEmptyBlock(el)) return
        el.innerHTML = editorMode ? '<br>' : '&nbsp;'
    })
}

export function typeableEditorChildren(root) {
    if (!root) return []
    return Array.from(root.children).filter(
        (el) => el.nodeType === 1 && (el.matches(TYPEABLE_BLOCK_SELECTOR) || el.tagName === 'DIV')
    )
}

export function ensureEditorEmptyBlocks(root) {
    if (!root || !root.querySelectorAll) return
    const blocks = [
        ...root.querySelectorAll(TYPEABLE_BLOCK_SELECTOR),
        ...Array.from(root.children).filter((el) => el.tagName === 'DIV')
    ]
    const seen = new Set()
    blocks.forEach((el) => {
        if (!el || seen.has(el)) return
        seen.add(el)
        if (!isVisuallyEmptyBlock(el)) return
        if (el.querySelector('br')) return
        el.appendChild(el.ownerDocument.createElement('br'))
    })
}

function rootHasVisibleContent(root) {
    if (!root) return false
    if (root.querySelector(SUBSTANTIVE_CHILD_SELECTOR)) return true
    const text = (root.textContent || '')
        .replace(/[\uFEFF\u200B]/g, '')
        .replace(/\u00A0/g, ' ')
        .trim()
    return Boolean(text)
}

/** Email / stored HTML → what Redactor v5 can caret into. */
export function htmlForEditor(html) {
    if (html == null || typeof html !== 'string') return html
    if (!html.trim()) return '<p><br></p>'
    return mapHtml(html, (root) => {
        decorateAnchorsInRoot(root)
        setEmptyParagraphs(root, true)
        // Loose text and inline markup (button labels, default text blocks) must stay.
        // Only a block with no visible content gets an empty paragraph.
        if (
            !root.querySelector(TYPEABLE_BLOCK_SELECTOR) &&
            !root.querySelector('div') &&
            !rootHasVisibleContent(root)
        ) {
            root.innerHTML = '<p><br></p>'
        }
    })
}

function stripZeroWidthText(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    const nodes = []
    while (walker.nextNode()) nodes.push(walker.currentNode)
    nodes.forEach((node) => {
        node.nodeValue = node.nodeValue.replace(/[\uFEFF\u200B]/g, '')
    })
}

/** Editor HTML → email-safe stored HTML. */
export function htmlForEmail(html) {
    if (html == null || typeof html !== 'string') return html
    if (!html.trim()) return '<p>&nbsp;</p>'
    const out = mapHtml(html, (root) => {
        stripZeroWidthText(root)
        stripAnchorsInRoot(root)
        setEmptyParagraphs(root, false)
    })
    return out && String(out).trim() ? out : '<p>&nbsp;</p>'
}

export function anchorEditorHtml(name) {
    const wrap = document.createElement('div')
    const el = document.createElement('a')
    el.setAttribute('name', name)
    el.setAttribute('id', name)
    el.setAttribute('href', '#' + name)
    decorateAnchorElement(el)
    wrap.appendChild(el)
    return wrap.innerHTML
}

export function applyAnchorEditorState(el, name) {
    if (!el) return
    el.setAttribute('name', name)
    el.setAttribute('id', name)
    el.setAttribute('href', '#' + name)
    decorateAnchorElement(el)
    el.textContent = ANCHOR_EMOJI
}

function seedEmptyBlockBr(el) {
    if (!el || !isVisuallyEmptyBlock(el) || el.querySelector('br')) return
    el.appendChild(el.ownerDocument.createElement('br'))
}

/**
 * v5 create('text') and clearContent() leave a bare <p></p>. Chrome will not
 * type into that. Seed a br instead, and do not move the caret.
 */
export function patchEmptyTextBlocks(app) {
    if (!app || !app.block || typeof app.block.create !== 'function') return () => {}

    const origCreate = app.block.create.bind(app.block)
    let patchedProto = null
    let origClear = null
    app.block.create = function (type = 'text', data = {}) {
        if ((type === 'text' || type == null) && (data.content === undefined || data.content === '')) {
            data = { ...data, content: '<br>' }
        }
        const block = origCreate(type, data)
        const proto = block && Object.getPrototypeOf(block)
        if (proto && typeof proto.clearContent === 'function' && !proto.clearContent._mpEmptyBr) {
            origClear = proto.clearContent
            patchedProto = proto
            proto.clearContent = function () {
                const result = origClear.call(this)
                seedEmptyBlockBr(this.el)
                return result
            }
            proto.clearContent._mpEmptyBr = true
        }
        return block
    }

    return () => {
        if (patchedProto && origClear) patchedProto.clearContent = origClear
        app.block.create = origCreate
    }
}

/**
 * Swallow addRange errors when Redactor focuses a node that is not in the
 * document (init rAF, click while the parser is still swapping nodes).
 */
export function patchSafeCaret(app) {
    const releases = []

    const wrap = (obj, name) => {
        if (!obj || typeof obj[name] !== 'function' || obj[name]._mpSafeCaret) return
        const orig = obj[name].bind(obj)
        obj[name] = function (...args) {
            const el = args[0] && typeof args[0].getEl === 'function' ? args[0].getEl() : args[0]
            const node = el && el.nodeType === 3 ? el.parentNode : el
            // caret.set removes the selection before addRange. A detached node
            // throws, and the editor is left with no caret.
            if (node && node.nodeType === 1 && !node.isConnected) return false
            try {
                return orig(...args)
            } catch (err) {
                return false
            }
        }
        obj[name]._mpSafeCaret = true
        releases.push(() => {
            obj[name] = orig
        })
    }

    wrap(app && typeof app.getService === 'function' ? app.getService('caret') : null, 'set')
    wrap(app && app.block, 'focus')

    return () => {
        releases.forEach((fn) => fn())
    }
}

/**
 * Cmd/Ctrl+A inside an editor that sits in the email layout table. Redactor
 * walks out of the editor and selects that cell, so the next delete wipes the
 * contenteditable. Select the editor instead. A table inside the editor is
 * left for Redactor (cell, then table, then all).
 */
export function keepSelectAllInEditor(event, editable) {
    if (!event || !editable) return false
    const key = event.key && String(event.key).toLowerCase()
    if (key !== 'a' || event.altKey || event.shiftKey) return false
    if (!(event.metaKey || event.ctrlKey)) return false
    const target = event.target
    if (!target || (target !== editable && !(editable.contains && editable.contains(target)))) return false
    const table = target.closest && target.closest('table')
    if (table && editable.contains(table)) return false

    event.preventDefault()
    event.stopPropagation()
    const doc = editable.ownerDocument
    const selection = doc.getSelection && doc.getSelection()
    if (selection) {
        const range = doc.createRange()
        range.selectNodeContents(editable)
        selection.removeAllRanges()
        selection.addRange(range)
    }
    const container = editable.closest && editable.closest('.rx-container')
    if (container) container.classList.add('rx-select-all')
    return true
}

function editorIsVisuallyEmpty(editable) {
    if (!editable) return true
    if (editable.querySelector(SUBSTANTIVE_CHILD_SELECTOR)) return false
    const text = (editable.textContent || '')
        .replace(/[\uFEFF\u200B]/g, '')
        .replace(/\u00A0/g, ' ')
        .trim()
    return !text
}

function editorHasCaret(editable) {
    const sel = editable.ownerDocument.getSelection && editable.ownerDocument.getSelection()
    if (!sel || !sel.rangeCount || !sel.anchorNode) return false
    // A caret on the contenteditable root is not inside a block, so typing
    // never lands in the paragraph.
    if (sel.anchorNode === editable) return false
    return editable.contains(sel.anchorNode)
}

function editorHasFocus(editable) {
    const active = editable.ownerDocument.activeElement
    return active === editable || editable.contains(active)
}

function placeCaretAtStart(app, el) {
    if (!el || !el.isConnected) return
    const caret = app && typeof app.getService === 'function' ? app.getService('caret') : null
    if (caret && typeof caret.set === 'function') {
        caret.set(el, 'start')
        return
    }
    const doc = el.ownerDocument
    const sel = doc.getSelection && doc.getSelection()
    if (!sel) return
    const range = doc.createRange()
    range.setStart(el, 0)
    range.collapse(true)
    sel.removeAllRanges()
    sel.addRange(range)
}

let repairingEmptyEditor = false

/**
 * Select-all + delete can remove every block, or leave a bare <p></p> with
 * the caret outside the editor. Put a typeable block back and, when the
 * editor is focused and empty, put the caret in it.
 */
export function repairEmptyEditor(app) {
    if (repairingEmptyEditor) return
    if (!app || typeof app.getEditor !== 'function') return
    const editable = app.getEditor()
    if (!editable || !editable.isConnected) return

    const blocks = [
        ...editable.querySelectorAll(TYPEABLE_BLOCK_SELECTOR),
        ...Array.from(editable.children).filter((el) => el.tagName === 'DIV' || el.tagName === 'P')
    ].filter((el, index, list) => el && el.isConnected && list.indexOf(el) === index)

    if (!blocks.length) {
        repairingEmptyEditor = true
        try {
            if (editorHasFocus(editable) && typeof app.setEmpty === 'function') {
                app.setEmpty()
                return
            }
            if (typeof app.clearContent === 'function') app.clearContent()
            if (app.blocks && typeof app.blocks.insertDefaultTextBlock === 'function') {
                app.blocks.insertDefaultTextBlock()
            }
        } finally {
            repairingEmptyEditor = false
        }
        return
    }

    ensureEditorEmptyBlocks(editable)
    if (!editorIsVisuallyEmpty(editable) || !editorHasFocus(editable) || editorHasCaret(editable)) return
    placeCaretAtStart(app, blocks[0])
}

let syncingEditorDom = false

/**
 * Keep anchors decorated and empty blocks holding a br. Never insert new
 * blocks or move the caret — that detaches Redactor ranges.
 */
export function syncEditorDom(app) {
    if (syncingEditorDom) return
    if (!app || typeof app.getEditor !== 'function') return
    const editable = app.getEditor()
    if (!editable) return

    syncingEditorDom = true
    try {
        decorateAnchorsInRoot(editable)
        ensureEditorEmptyBlocks(editable)
    } finally {
        syncingEditorDom = false
    }
}
